import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, effect, input, ChangeDetectionStrategy } from '@angular/core';
import { Observable, Subscription, debounceTime } from 'rxjs';

import { FilterService } from './filter.service';
import { FilterField, Filters, TabbedFilterField } from './filters';

import { BreakpointObserver, BreakpointState, Breakpoints } from '@angular/cdk/layout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { SavedFilter } from './filter.service';
import { TabFilterTheme } from './filters/tabbed/filter-field-tabbed.component';

@Component({
  selector: 'va-filter-container',
  template: `
    <div class="filter-container">
      @for (field of tabbedFilters; track field) {
        <va-filter-tabbed
          (filterApplied)="handleFilterValueChanged($event)"
          [filterField]="field"
          [theme]="theme"
          [useCustomCase]="useCustomCase"
          />
      }
      @if (showToolbar) {
        <div class="toolbar">
          <div class="table-controls-row">
            @if (hasSideBarFields()) {
              <button
                mat-stroked-button
                class="filter-button"
                (click)="toggleFilterOpen()"
                [ngClass]="{ selected: filterIsOpen && !filtersDisabled }"
                [hidden]="filtersDisabled"
                [disabled]="filtersDisabled"
                data-action="clicked-filter-button"
                >
                <mat-icon>filter_list</mat-icon>
                {{ 'FRONTEND.UIKIT.VA_FILTER.FILTERS' | translate }}
              </button>
            }
            <div class="flex-helper">
              <div class="left-toolbar-item">
                <ng-content select="[leftToolbarItem]" />
              </div>
            </div>
            <div class="search-and-text">
              @if (searchable) {
                @if (enabledSaveSearchTerm || contextKey) {
                  <va-search-box
                    (update)="searchTermChanged.emit($event)"
                    [initial]="initialSearch"
                    [disabled]="searchDisabled()"
                    [enabledSaveSearchTerm]="enabledSaveSearchTerm"
                    [contextKey]="contextKey"
                    [placeholder]="placeholder"
                    />
                } @else {
                  <glxy-form-field suffixIcon="search" class="search-input" [bottomSpacing]="'none'">
                    <input type="search" placeholder="Search" matInput [formControl]="searchControl" />
                  </glxy-form-field>
                }
              }
              <div class="toolbar-text">{{ toolbarText }}</div>
            </div>
            <ng-content select="[header]" />
          </div>
        </div>
      }
      <div class="flex-row">
        @if (!filtersDisabled && !!filters) {
          <div class="filter-wrapper" [ngClass]="{ hide: !filterIsOpen }">
            <va-filter
              [showFilterSharing]="showFilterSharing"
              [filters]="filters"
              [savedFilters]="savedFilters"
              (savedFilterSelected)="savedFilterSelected.emit($event)"
              (saveCurrentFilter)="saveCurrentFilter.emit($event)"
              (removeSavedFilter)="removeSavedFilter.emit($event)"
              (fieldValueChanged)="fieldValueChanged.emit($event)"
              (exportFilterDialog)="exportFilterDialog.emit($event)"
              (importFilterDialog)="importFilterDialog.emit($event)"
              />
          </div>
        }
        <div class="content-container" [ngClass]="{ 'top-border': filters && filters.activeFields.length > 0 }">
          @if (filters) {
            <va-applied-filters-bar
              (fieldValueChanged)="handleFilterValueChanged($event)"
              [filters]="filters"
              />
          }
          <div>
            <ng-content select="[content]" />
          </div>
        </div>
      </div>
    </div>
    `,
  styleUrls: ['filter-container.component.scss'],
  providers: [FilterService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FilterContainerComponent implements OnInit, OnDestroy {
  @Input() showFilterSharing? = false;
  @Input() searchable = true;
  @Input() toolbarText: string;
  @Input() showToolbar = true;
  protected readonly searchDisabled = input(false);
  @Input() filtersDisabled = false;
  @Input() filterIsOpen = false;
  @Input() initialSearch = '';
  @Input() placeholder = '';
  subscriptions: Subscription[] = [];

  @Input() filters: Filters;
  @Input() savedFilters: Observable<SavedFilter[]>;
  @Input() theme: TabFilterTheme = 'gradient';
  @Input() useCustomCase = false;
  @Input() enabledSaveSearchTerm = false;
  @Input() contextKey = '';

  @Output() searchTermChanged = new EventEmitter();
  @Output() fieldValueChanged = new EventEmitter();

  @Output() savedFilterSelected = new EventEmitter();
  @Output() removeSavedFilter = new EventEmitter();
  @Output() saveCurrentFilter = new EventEmitter();
  @Output() exportFilterDialog = new EventEmitter();
  @Output() importFilterDialog = new EventEmitter();

  searchControl = new FormControl<string>('');

  constructor(private breakPointObserver: BreakpointObserver) {
    this.searchControl.valueChanges.pipe(takeUntilDestroyed(), debounceTime(200)).subscribe((value) => {
      this.searchTermChanged.emit(value);
    });

    effect(() => {
      if (this.searchDisabled()) {
        this.searchControl.disable();
      } else {
        this.searchControl.enable();
      }
    });
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.breakPointObserver.observe(Breakpoints.XSmall).subscribe((state: BreakpointState) => {
        if (state.matches) {
          this.filterIsOpen = false;
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => {
      sub.unsubscribe();
    });
  }

  hasSideBarFields(): boolean {
    if (this.filters != null) {
      return this.filters.fields.filter((field: FilterField<any>) => !(field instanceof TabbedFilterField)).length > 0;
    } else {
      return false;
    }
  }

  toggleFilterOpen(): void {
    if (this.filtersDisabled) {
      return;
    }
    this.filterIsOpen = !this.filterIsOpen;
  }

  get tabbedFilters(): TabbedFilterField[] {
    if (this.filters != null) {
      return this.filters.fields.filter((field) => field instanceof TabbedFilterField) as TabbedFilterField[];
    } else {
      return [];
    }
  }

  handleFilterValueChanged(event: FilterField<any>): void {
    this.fieldValueChanged.emit(event);
  }
}
